/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {createRef} from "react";
import {currentTimeMillies, filterDuplicates, sortArray} from "@intuitionrobotics/ts-common";
import {Coordinates, D3ChartData, GraphsModule} from "../../../_to_remove/GraphsModule";
import {COLORS} from "@res/colors";
import {FONTS} from "@res/fonts";
import {HGap} from "../../widget/HGap";
import {Card} from "../../widget/Card";
import {CardHeader} from "../../widget/CardHeader";
import {graphBodyStyle, graphScroll} from "./GraphPanelWrapper";
import dayjs from "../../../utils/dayjs";
import * as emotion from "emotion";
import {DB_Patient} from "@app/app-shared/patient";
import {OnTimeFilterChanged, UserPreferencesModule} from "@modules/UserPreferencesModule";
import SelfReportGraph from "./SelfReportGraph";
import {GraphUtil} from "./GraphUtil";
import {PatientsDataModule} from "@modules/PatientDataModule";
import {DB_PatientData, TableKeys} from "@app/app-shared/patient-data";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {OnWindowResize} from "../../../App";
import BloodPressureGraph from "../../../_to_remove/BloodPressureGraph";
import {Key_After_Breakfast, Key_After_Dinner, Key_After_Lunch, Key_Before_Breakfast, Key_Before_Dinner, Key_Before_Lunch, MeasurementRange} from "@app/app-shared/care_config";
import {Spacing} from "@styles/styles";
import {showTooltipAtXY} from "../../widget/appTooltip";
import {formatAlertDatetime} from "../alerts/util";

type State = {
    hoverDate: number,
    hoverId: string,
    graph_day_width: number,
    number_of_days: number
};

type Props = {
    settings: { label: string, text: string, color: string, validRanges?: MeasurementRange, icon?: any }[],
    range: { minRange: number, maxRange: number },
    title: string,
    measurement: string,
    patient: DB_Patient
    renderYAxisTick: (i: string) => React.ReactNode;
    yTickSpacing: number
    inGraphLegend?: boolean
    lines?: boolean
    isBloodPressure?: boolean
    valuesSuffix?: string
};

const wrapper = emotion.css(
    {
        boxSizing: "border-box",
        display: "flex",
        marginBottom: "12px"
    }
);

export class SelfReportGraphWrapper
    extends BaseComponent<Props, State>
    implements OnTimeFilterChanged, OnWindowResize {

    private readonly MIN_DAY_WIDTH = 110;

    private graphRef: React.RefObject<HTMLDivElement> = createRef<HTMLDivElement>();
    public static defaultProps: Partial<Props> = {
        renderYAxisTick: (i: string) => <>{i}</>,
        valuesSuffix: "",
        yTickSpacing: 50
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            hoverDate: currentTimeMillies(),
            hoverId: "",
            graph_day_width: this.MIN_DAY_WIDTH,
            number_of_days: UserPreferencesModule.getTimeFilterSelectedOption().days
        };
    }

    __onWindowResize = () => {
        this.calcGraphWidth();
    };

    componentDidMount() {
        this.calcGraphWidth();
    }

    __onPatientDataUpdated() {
        this.setStateToLastDataPoint();
    }

    private setStateToLastDataPoint() {
        if (this.props.isBloodPressure) {
            const data = PatientsDataModule.getData(this.props.patient?.unitId || "", TableKeys.SelfReport);

            const patientData: DB_PatientData[] | undefined = data?.data?.filter(_data => _data.label === "systolic" || _data.label === "heart_rate");
            this.setState({hoverDate: patientData ? sortArray(patientData, _element => _element.timestamp)[0]?.timestamp : currentTimeMillies(), hoverId: ""});
        }
    }

    __onTimeFilterChanged() {
        this.setState({number_of_days: UserPreferencesModule.getTimeFilterSelectedOption().days, graph_day_width: this.MIN_DAY_WIDTH});
    }

    calcGraphWidth = () => {
        const width = this.state.graph_day_width;
        const graph = this.graphRef;
        if (!graph || !graph.current)
            return;

        const diff = graph.current.offsetWidth - this.state.number_of_days * width;
        this.setState({graph_day_width: Math.max(this.MIN_DAY_WIDTH, width + diff / this.state.number_of_days)});
    };

    getData = (): D3ChartData[] => {
        const labels = this.props.settings.map(l => l.label);
        let coordinateData: Coordinates[][];

        if (this.props.isBloodPressure) {
            const patientData = labels.map(l => PatientsDataModule.spreadSelfReportData([l], this.props.patient?.unitId));
            coordinateData = patientData?.map(p => p.map(
                _datatype => ({x: _datatype.timestamp, y: _datatype.value, _id: _datatype._id})));

        } else {

            const patientData = PatientsDataModule.spreadSelfReportData(labels, this.props.patient?.unitId);
            coordinateData = labels.map(
                l => patientData.filter(d => d.label === l)?.map(_datatype => ({x: _datatype.timestamp, y: _datatype.value, _id: _datatype._id})));
        }

        if (!coordinateData || coordinateData.every(c => c.length === 0))
            return [];

        return labels.map((l, index) => GraphUtil.d3ChartData(l, coordinateData[index],
            this.props.settings.filter(elem => elem.label === l)[0].color));
    };

    getOriginalData = () => {
        const data = PatientsDataModule.getData(this.props.patient?.unitId || "", TableKeys.SelfReport);

        if (!data) {
            return [];
        }

        const labels = this.props.settings.map(l => l.label);

        const patientData = data?.data;
        if (!patientData || patientData.length === 0)
            return [];

        const sortedData = labels.map(l => sortArray(
            patientData.filter(_data => _data.label === l).map(_datatype => ({x: _datatype.timestamp, y: _datatype.value, _id: _datatype._id})),
            _data => _data.x, true));
        if (sortedData.length === 0)
            return [];

        return labels.map((l, index) => GraphUtil.d3ChartData(l, sortedData[index], this.props.settings.filter(elem => elem.label === l)[0].color));
    };

    yScale = () => GraphsModule.yScale(this.props.range.minRange,
        this.props.range.maxRange,
        190);

    getYAxis = () => {
        const ticks = [];
        const yScale = this.yScale();
        for (let i = this.props.range.maxRange; i >= this.props.range.minRange; i = i - this.props.yTickSpacing) {
            ticks.push(<div
                key={i}
                style={{
                    fontSize: 12,
                    color: COLORS.neutral_text_dark_2,
                    position: "absolute",
                    right: "10px",
                    top: yScale(i) + 10
                }}>
                {this.props.renderYAxisTick(`${i}${this.props.valuesSuffix}`)}
            </div>);
        }
        return ticks;
    };

    renderDetailsCards = () => {
        const day = dayjs(this.state.hoverDate);

        const displayDayName = day.startOf("day").isSame(dayjs().startOf("day")) ? "Today" : day.format("dddd");

        return <div
            className={`ll_v_s`}
            style={{boxShadow: "0 1px 8px 0 #ececec", height: "375px", flex: 1, backgroundColor: COLORS.white, borderRadius: 4}}>
            <div style={{boxShadow: "0 1px 8px 0 #ececec", padding: "10px", display: "flex", borderRadius: 6}}>
                {FONTS.bold_16(day.format("M/D"))}
                <HGap h={4}/>
                {FONTS.reg_16(displayDayName, {color: "#a4a6a8"})}
            </div>
            <div style={{justifyContent: "space-between", flexGrow: 1, overflow: "scroll"}}>
                <div style={{
                    display: "flex",
                    justifyContent: "space-around",
                    marginLeft: "4px",
                    marginRight: "4px",
                    paddingTop: "15px",
                    paddingBottom: "7px",
                    borderBottom: "solid",
                    borderWidth: "1px",
                    borderColor: "#ececee"
                }}>
                    <div style={{flex: this.props.isBloodPressure ? 4 : 3, paddingLeft: 10}}>
                        {FONTS.reg_11(this.props.isBloodPressure ? "Time" : "Date", {color: "#a4a6a8"})}
                    </div>
                    <div style={{
                        flex: "2 1 0"
                    }}>
                        {FONTS.reg_11(this.props.measurement, {color: "#a4a6a8"})}
                    </div>
                    {this.props.isBloodPressure && <div style={{
                        flex: "2",
                        textAlign: "center"
                    }}>
                        {FONTS.reg_11("Pulse", {color: "#a4a6a8"})}
                    </div>}
                </div>
                {this.renderDetailRows()}
            </div>
        </div>;
    };

    private renderDetailRows() {

        if (!this.props.isBloodPressure) {
            const labels = this.props.settings.map(l => l.label);
            const patientData = PatientsDataModule.getData(this.props.patient?.unitId || "", TableKeys.SelfReport);

            if (!patientData)
                return;

            const data = patientData?.data?.filter(d => labels.includes(d.label)) || [];
            sortArray(data, _data => _data.timestamp);

            return <>
                {filterDuplicates(data).map(d => {
                        const validRanges = this.props.settings.find(s => s.label === d.label)?.validRanges?.[d.label];
                        const outOfRange: { [key: string]: boolean } = {};

                        outOfRange[d.label] = validRanges?.min && validRanges?.max ? (d.value < validRanges.min || d.value > validRanges.max) : false;
                        return this.renderDetailRow(d.timestamp, d.value, outOfRange, d._id);
                    }
                )}
            </>;
        } else {
            const data = this.getOriginalData();

            const hoverValues = data.map(_data => {
                _data.data = _data.data.filter(_coordinate => dayjs(_coordinate.x).format("DD-MM-YY") === dayjs(this.state.hoverDate).format("DD-MM-YY"));
                return _data;
            });
            const pulse = hoverValues.find(_value => _value.label === "heart_rate");
            const systolic = hoverValues.find(_value => _value.label === "systolic");
            const diastolic = hoverValues.find(_value => _value.label === "diastolic");

            if (systolic)
                sortArray(systolic.data, _data => _data.x);

            if (pulse)
                sortArray(pulse.data, _data => _data.x);

            return <>
                {(systolic && systolic.data.length !== 0) && filterDuplicates(systolic.data).map(_systolic => {
                    const diastolicMatch = diastolic?.data.find(_diastolic => _diastolic.x === _systolic.x);

                    let pulseMatch: Coordinates | undefined;

                    if (pulse?.data) {
                        // pulseMatch = pulse.data.find(_pulse => _pulse.x === _systolic.x);
                        pulseMatch = pulse.data.find(
                            _pulse => _pulse._id === _systolic._id || (dayjs(_pulse.x).isBetween(dayjs(_systolic.x).subtract(5, "m"), dayjs(_systolic.x).add(5, "m"))));

                        pulse.data = pulse.data.filter(d => d._id !== pulseMatch?._id);
                    }
                    const outOfRange: { [key: string]: boolean } = {};

                    let validRanges = this.props.settings.find(s => s.label === systolic.label)?.validRanges?.[systolic.label];
                    outOfRange[systolic.label] = validRanges?.min && validRanges?.max ? (_systolic.y < validRanges.min || _systolic.y > validRanges.max) : false;

                    if (diastolic && diastolicMatch) {
                        validRanges = this.props.settings.find(s => s.label === diastolic.label)?.validRanges?.[diastolic?.label];
                        outOfRange[diastolic?.label] = validRanges?.min && validRanges?.max ? (diastolicMatch?.y < validRanges.min || diastolicMatch?.y > validRanges.max) : false;
                    }
                    if (pulse && pulseMatch) {
                        validRanges = this.props.settings.find(s => s.label === pulse.label)?.validRanges?.[pulse.label];
                        outOfRange[pulse?.label] = validRanges?.min && validRanges?.max ? (pulseMatch?.y < validRanges.min || pulseMatch?.y > validRanges.max) : false;
                    }
                    return this.renderDetailRow(_systolic.x, _systolic.y, outOfRange, _systolic._id, diastolicMatch?.y, pulseMatch?.y);
                })}
                {(pulse && pulse.data.length !== 0) && filterDuplicates(pulse?.data || []).map(_pulse => {
                    const outOfRange: { [key: string]: boolean } = {};
                    const validRanges = this.props.settings.find(s => s.label === pulse.label)?.validRanges?.[pulse.label];

                    outOfRange[pulse.label] = validRanges?.min && validRanges?.max ? (_pulse.y < validRanges.min || _pulse.y > validRanges.max) : false;
                    return this.renderDetailRow(_pulse.x, NaN, outOfRange, _pulse._id, undefined, _pulse.y);
                })}</>;
        }
    }

    renderDetailRow = (timestamp: number, data: number, isOutOfRange: { [key: string]: boolean }, id?: string, diastolic?: number, pulse?: number) => {
        const formattedTime = formatAlertDatetime(timestamp);
        const currLabels = Object.keys(isOutOfRange);
        return <div
            key={id}
            style={{
                display: "flex",
                justifyContent: "space-around",
                backgroundColor: (dayjs(this.state.hoverDate).startOf("day").valueOf() === dayjs(timestamp).startOf(
                    "day").valueOf() && this.state.hoverId === id) ? "#fff9ec" : COLORS.white,
                padding: "4px",
                borderBottom: "solid",
                borderWidth: "1px",
                borderColor: "#ececee"
            }}>
            {!this.props.isBloodPressure && <div style={{flex: 1, paddingLeft: 10}}>
                {FONTS.reg_11(`${formattedTime.date}`, {color: "#a4a6a8"})}
            </div>}
            <div style={{flex: 2, paddingLeft: 10}}>
                {FONTS.reg_11(`${dayjs(formattedTime.time, "H:mm").format("hh:mm A")}`, {color: "#a4a6a8"})}
            </div>
            <div style={{
                flex: 1
            }}>
                {!this.props.isBloodPressure ? FONTS.reg_11(`${data ? Math.floor(data * 100) / 100 : "--"}${this.props.valuesSuffix}`,
                        {color: isOutOfRange?.[currLabels[0]] ? "red" : "#a4a6a8"}) :
                    <div style={{fontFamily: "Inter-Regular", fontSize: "11px", outlineColor: "#a4a6a8"}}>{FONTS.reg_11(`${data ? Math.floor(data) : "--"}`,
                        {color: data && isOutOfRange?.["systolic"] ? "red" : "#a4a6a8"})} / {FONTS.reg_11(
                        `${diastolic ? Math.floor(diastolic) : "--"}`, {color: diastolic && isOutOfRange?.["diastolic"] ? "red" : "#a4a6a8"})}</div>}
            </div>
            <div style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            }}>
                {this.props.isBloodPressure &&
                    FONTS.reg_11(`${pulse ? Math.floor(pulse) : "--"}`, {color: isOutOfRange?.["heart_rate"] ? "red" : "#a4a6a8"})}
            </div>
        </div>;
    };

    private static getColoredDot(color?: string, _css?: {}) {

        return <div style={{
            backgroundColor: color,
            width: "7px",
            height: "7px",
            borderRadius: 10,
            ..._css
        }}/>;
    }

    renderLegend = () => {
        const data = this.getOriginalData();

        if (this.props.isBloodPressure) {

            const sums = data.map(() => 0);
            const avgs = data.map(() => 0);

            for (const index in data) {
                data[index].data.forEach(_data => sums[index] += Number(_data.y));
                avgs[index] = sums[index] / data[index].data.length;
            }

            const textComponents = [FONTS.reg_11(`Average:`),
                <br/>];
            const isThereBP = avgs[0] && avgs[1];
            if (isThereBP) {
                textComponents.push(FONTS.reg_11_bold(`${Math.floor(avgs[0] * 100) / 100}/${Math.floor(avgs[1])}`));
                textComponents.push(FONTS.reg_11(`${this.props.measurement}`));
            }
            if (avgs[2]) {
                textComponents.push(FONTS.reg_11_bold(`${isThereBP ? '|' : ''}`));
                textComponents.push(FONTS.reg_11_bold(`${Math.floor(avgs[2])}`));
                textComponents.push(FONTS.reg_11(`bpm`));
            }
            return GraphUtil.renderGraphLegend([textComponents], COLORS.very_light_blue);
        } else {
            let sum = 0;

            for (const d of data) {
                d.data.forEach(_data => sum += (Number(_data.y) || 0));
            }
            const average = sum / data.reduce((acc, curr) => acc + curr.data.length, 0);

            return GraphUtil.renderGraphLegend(
                [[FONTS.reg_11(`Average:`),
                    FONTS.reg_11_bold(`${Math.floor(average * 100) / 100}${this.props.valuesSuffix} ${this.props.measurement}`)]], COLORS.very_light_blue);
        }
    };

    renderInGraphLegend = () => {
        const _css = {
            borderStyle: "solid",
            borderWidth: "2px",
            borderColor: "red",
            backgroundColor: "rgba(0, 0, 0, 0)"
        };
        let nodes: React.ReactNode[][] = [];

        if (this.props.inGraphLegend) {
            nodes = nodes.concat(this.props.settings.map((l, idx) => [!!l.icon ? <img alt={l.label} src={l.icon} height={20} width={20}/>
                : SelfReportGraphWrapper.getColoredDot(l.color),
                FONTS.reg_11_secondary_dark(l.text), ...(idx === this.props.settings.length - 1 ? [<br/>] : [])]));
        }

        nodes.push([SelfReportGraphWrapper.getColoredDot("red", _css),
            FONTS.reg_11_secondary_dark("Out of healthy range")]);

        return GraphUtil.renderGraphLegend(nodes);
    };

    render() {
        const data = this.getData();
        if (!data || data.length === 0)
            return null;

        const labels = this.props.settings.map(l => l.label);
        const shareInfo = PatientsDataModule.getData(this.props.patient.unitId, TableKeys.SelfReport)?.share_info;
        const sharingInfos = labels.map(l => shareInfo?.[l]);
        const notShared = sharingInfos.some(c => c === false);

        return <div key={this.props.title.replace(" ", "_")} className={`${wrapper}`}>
            <Card style={{flex: 1}}>
                <CardHeader>
                    <div style={{justifyContent: "left", display: "flex", flex: 1}}>
                        <div className={"ll_h_c"}>
                            {FONTS.medium_18_graysdark(this.props.title, {whiteSpace: "nowrap"})}
                        </div>
                        <HGap h={8}/>
                        <div className={"ll_h_c"} style={{display: "table-cell", verticalAlign: "middle"}}>
                            {notShared && FONTS.bold_11_secondary_red(
                                "Customer stopped sharing information", {
                                    height: 24, padding: `0 ${Spacing.m}px`, backgroundColor: "rgb(255 47 47 / 48%)",
                                    display: "table-cell", verticalAlign: "middle"
                                })}
                        </div>
                        <HGap h={8}/>
                        <div style={{justifyContent: "space-between", display: "flex", flex: 1}}>
                            {this.renderLegend()}
                            <HGap h={50}/>
                            {this.renderInGraphLegend()}
                        </div>
                    </div>
                </CardHeader>
                {this.renderGraph(data)}
            </Card>
            {this.renderDetailsCards()}
        </div>;
    }

    private renderGraph = (data: D3ChartData[]) => {
        return (<>
            <div className={graphBodyStyle}>
                <div className={"ll_v_s"}
                     style={{
                         justifyContent: "space-around",
                         flex: "0 0 35px",
                         height: 245,
                         borderRight: `1px solid ${COLORS.neutral_gray_1}`,
                         position: "relative"
                     }}>
                    {this.getYAxis()}
                </div>
                <div style={{width: 0, display: "flex", flex: 1}} onMouseLeave={() => this.setStateToLastDataPoint()}>
                    <div ref={this.graphRef} className={graphScroll} style={{
                        width: "100%",
                        overflow: "auto",
                        position: "relative",
                        display: "flex",
                        flexDirection: "row-reverse"
                    }}>
                        {this.renderActualGraph(data)}
                    </div>
                </div>
            </div>
        </>);
    };

    private renderActualGraph = (data: D3ChartData[]) => {
        const validRanges: { [key: string]: { min: number, max: number } | undefined } = {};

        for (const s of this.props.settings) {
            validRanges[s.label] = s.validRanges?.[s.label];
        }

        if (this.props.isBloodPressure) {
            return <BloodPressureGraph
                patientId={this.props.patient._id}
                frames={{startFrame: this.props.range.minRange, endFrame: this.props.range.maxRange, min: this.props.range.minRange, max: this.props.range.maxRange}}
                data={data}
                timeframe={90}
                viewBox={this.state.number_of_days}
                borderBox={true}
                sizing={{height: 250, width: this.state.graph_day_width * this.state.number_of_days}}
                validRanges={validRanges}
                onHover={(id: string, date: number) => this.setState({hoverDate: date, hoverId: id})}
            />
        }


        return <SelfReportGraph
            patientId={this.props.patient._id}
            lines={this.props.lines || false}
            frames={{startFrame: this.props.range.minRange, endFrame: this.props.range.maxRange, min: this.props.range.minRange, max: this.props.range.maxRange}}
            data={data}
            timeframe={90}
            viewBox={this.state.number_of_days}
            borderBox={true}
            sizing={{height: 250, width: this.state.graph_day_width * this.state.number_of_days}}
            validRanges={validRanges}
            onHover={(id: string, date: number, rect?: DOMRect, label?: string) => {
                this.setState({hoverDate: date, hoverId: id}, () => {
                    if (rect && label && ["fasted",
                        "postprandial",
                        Key_Before_Breakfast,
                        Key_After_Breakfast,
                        Key_Before_Lunch,
                        Key_After_Lunch,
                        Key_Before_Dinner,
                        Key_After_Dinner].includes(label)) {
                        showTooltipAtXY(label.replace("_", " "), rect.x - 20, rect.y - 50);
                    }
                });
            }}
        />
    };
}
